import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import type { BoothTrack } from '../../panels';
import { postPositions } from '../../AssortmentPanel/useBoothBasket';
import type { BasketPositionInput } from '../../AssortmentPanel/useBoothBasket';
import { VTS_BASKET_QUERY_KEY } from '../../AssortmentPanel/useBoothProducts';
import type { IBoothNewProduct } from '../types';
import styles from '../HighlightsPanel.module.scss?module';

interface NewProductTileProps {
  product: IBoothNewProduct;
  track: BoothTrack;
}

const PRICE_FORMAT = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'EUR',
});
/** How long "Added" stays on the button. */
const ADDED_RESET_MS = 2500;
/** Upper bound of the quantity field. */
const MAX_UNITS = 99999;

/** A new listing of the brand with quantity and a direct add to the VTS basket. */
export default function NewProductTile({
  product,
  track,
}: NewProductTileProps) {
  const queryClient = useQueryClient();
  const quantityId = useId();
  const [quantity, setQuantity] = useState('1');
  const [added, setAdded] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addMutation = useMutation({ mutationFn: postPositions });

  useEffect(() => {
    return () => {
      if (resetTimer.current !== null) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  const units = parseUnits(quantity);
  const imageUrl =
    product.image_transparent_public_url ||
    product.image_background_public_url ||
    product.product_attribute.image_white_public_url;

  const handleAdd = () => {
    if (units < 1) {
      return;
    }
    const positions: BasketPositionInput[] = [
      {
        product_attribute_id: product.product_attribute.id,
        quantity: units,
        price: product.current_price,
      },
    ];
    addMutation.mutate(positions, {
      onSuccess: () => {
        setQuantity('1');
        setAdded(true);
        track('basket_added', {
          product_size_id: product.product_size.erp_id,
          units,
          source: 'highlights',
        });
        queryClient.invalidateQueries({ queryKey: VTS_BASKET_QUERY_KEY });
        if (resetTimer.current !== null) {
          clearTimeout(resetTimer.current);
        }
        resetTimer.current = setTimeout(() => {
          resetTimer.current = null;
          setAdded(false);
        }, ADDED_RESET_MS);
      },
    });
  };

  return (
    <article className={styles.productTile}>
      <div className={styles.productImage}>
        {imageUrl && <img src={imageUrl} alt="" loading="lazy" />}
        <span className={styles.newBadge}>New listing</span>
      </div>
      <div className={styles.productBody}>
        <h3 className={styles.productName}>{product.product_size.name}</h3>
        <p className={styles.productFlavour}>
          {product.product_attribute.name}
        </p>
        <p className={styles.productPrice}>
          {PRICE_FORMAT.format(product.current_price)}
        </p>
        <div className={styles.productControls}>
          <label htmlFor={quantityId} className={styles.visuallyHidden}>
            Quantity of {product.product_size.name},{' '}
            {product.product_attribute.name}
          </label>
          <input
            id={quantityId}
            className={styles.quantityInput}
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_UNITS}
            step={1}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            onBlur={() => setQuantity(String(units))}
          />
          <button
            type="button"
            className={styles.primaryButton}
            disabled={addMutation.isPending || units < 1}
            onClick={handleAdd}
          >
            {added ? (
              <>
                <FontAwesomeIcon icon={faCheck} /> Added
              </>
            ) : (
              'Add to basket'
            )}
          </button>
        </div>
        <p className={styles.visuallyHidden} role="status">
          {added ? 'Added to basket' : ''}
        </p>
        {addMutation.isError && (
          <p className={styles.errorText} role="alert">
            Could not add to basket. Please try again.
          </p>
        )}
      </div>
    </article>
  );
}

/**
 * Parses typed input to a whole number of units from 0 to MAX_UNITS. Only the leading digits
 * count, so "1.5" is 1, not 15; empty input is 0 and disables the add.
 */
function parseUnits(value: string): number {
  const digits = /^\d+/.exec(value.trim());
  const units = digits ? Number.parseInt(digits[0], 10) : 0;

  return Math.min(units, MAX_UNITS);
}
